import { defineStore } from 'pinia';
import { ref } from 'vue';

import { play, type SoundName } from '@/audio';

export type NotificationLevel = 'info' | 'success' | 'warning' | 'error';

export interface Notification {
  id: number;
  level: NotificationLevel;
  message: string;
}

let nextId = 1;

export const useNotificationStore = defineStore('notifications', () => {
  const items = ref<Notification[]>([]);

  /** Every toast carries a sound, so feedback is heard as well as seen. */
  const SOUND_FOR: Record<NotificationLevel, SoundName> = {
    success: 'confirm',
    error: 'error',
    warning: 'cancel',
    info: 'clickSoft',
  };

  function push(level: NotificationLevel, message: string, ttlMs = 6000): number {
    play(SOUND_FOR[level]);
    const id = nextId++;
    items.value.push({ id, level, message });
    // Never let the stack grow past what fits on screen.
    if (items.value.length > 5) items.value.shift();
    window.setTimeout(() => dismiss(id), ttlMs);
    return id;
  }

  function dismiss(id: number): void {
    items.value = items.value.filter((n) => n.id !== id);
  }

  return {
    items,
    dismiss,
    info: (m: string) => push('info', m),
    success: (m: string) => push('success', m),
    warning: (m: string) => push('warning', m),
    error: (m: string) => push('error', m, 9000),
  };
});
